
import { DataBox, createDataBox } from 'app/common/data-box-store'


type ColumnItemsType = {
  [k: string]: boolean
}
type RowItemsType = {
  [k: string]: boolean | null
}

type TableSelectedRowColumnState = {
  rowItems: RowItemsType
  rowItemsDisabled: Record<string, boolean>
  columnItems: Record<string, ColumnItemsType>
  columnItemsDisabled: Record<string, ColumnItemsType>
  count: number
  checkedAll: boolean | null
}

const tableSelectedRowColumnStoreID = 'tableSelectedRowColumnStoreID'
const defaultState: TableSelectedRowColumnState = {
  rowItems: {},
  rowItemsDisabled: {},
  columnItems: {},
  columnItemsDisabled: {},
  count: 0,
  checkedAll: false,
}

const isCheckedAll = (
  statusState: RowItemsType,
  disabledStatus?: ColumnItemsType,
): boolean | null => {
  const filteredValues = Object.entries(statusState)
    .filter(([key]) => !disabledStatus || !disabledStatus[key])
    .map(([, value]) => value)
  if (filteredValues.includes(null)) {
    return null
  }

  const allTrue = filteredValues.every(value => value === true)
  if (allTrue) {
    return true
  }

  const allFalse = filteredValues.every(value => value === false)
  if (allFalse) {
    return false
  }

  return null
}

class SelectedRowColumnDataBox extends DataBox<TableSelectedRowColumnState> {
  selectToggleRow = (rowID: string, status: boolean) => {
    const updateColumnStatus = Object.keys(this.data.columnItems[rowID]).reduce((result, key) => {
      return {
        ...result,
        [key]: this.data.columnItemsDisabled[rowID][key] ? this.data.columnItems[rowID][key] : status,
      }
    }, {} as { [k: string]: boolean })

    const newRowState = {
      ...this.data.rowItems,
      [rowID]: status,
    }

    const newColumnState = {
      ...this.data.columnItems,
      [rowID]: updateColumnStatus,
    }
    const count = Object.values(newRowState).filter(value => value).length
    this.updateData(() => ({
      ...this.data,
      rowItems: newRowState,
      columnItems: newColumnState,
      count,
      checkedAll: isCheckedAll(newRowState),
    }))
  }

  selectToggleColumn = (rowID: string, columnID: string, status: boolean) => {
    const updateColumnStatus: ColumnItemsType = {
      ...this.data.columnItems[rowID],
      [columnID]: status,
    }
    const newRowState = {
      ...this.data.rowItems,
      [rowID]: isCheckedAll(updateColumnStatus, this.data.columnItemsDisabled[rowID]),
    }
    const newColumnState = {
      ...this.data.columnItems,
      [rowID]: updateColumnStatus,
    }
    const count = Object.values(newRowState).filter(value => value).length
    this.updateData(() => ({
      ...this.data,
      rowItems: newRowState,
      columnItems: newColumnState,
      count,
      checkedAll: isCheckedAll(newRowState),
    }))
  }

  initColumnDisabled = (rowID: string, columnID: string, disabled: boolean) => {
    const updateColumnDisabled = {
      ...this.data.columnItemsDisabled[rowID],
      [columnID]: disabled,
    }

    const newColumnDisabledState = {
      ...this.data.columnItemsDisabled,
      [rowID]: updateColumnDisabled,
    }

    const newRowDisabledState = {
      ...this.data.rowItemsDisabled,
      [rowID]: Object.values(updateColumnDisabled).every(item => item === true),
    }

    this.updateData(() => ({
      ...this.data,
      columnItemsDisabled: newColumnDisabledState,
      rowItemsDisabled: newRowDisabledState,
    }))
  }

  setCurrentAsDefault = () => {
    this.setDefault({ ...this.data })
  }

  resetToInitState = () => {
    this.setDefault(defaultState)
    this.resetToDefault()
  }

  selectAllRowsToggle = (status: boolean) => {
    const newRowState = { ...this.data.rowItems }
    const newColumnState = { ...this.data.columnItems }
    const allRowIDs = Object.keys(newColumnState)
    allRowIDs.forEach((rowID) => {
      const updateColumnStatus = Object.keys(this.data.columnItems[rowID]).reduce((result, key) => {
        return {
          ...result,
          [key]: this.data.columnItemsDisabled[rowID][key] ? this.data.columnItems[rowID][key] : status,
        }
      }, {} as { [k: string]: boolean })
      newColumnState[rowID] = updateColumnStatus
      newRowState[rowID] = this.data.rowItemsDisabled[rowID] ? this.data.rowItems[rowID] : status
    })

    this.updateData(() => ({
      ...this.data,
      rowItems: newRowState,
      columnItems: newColumnState,
      count: status ? allRowIDs.length : 0,
      checkedAll: status,
    }))
  }

  numberOfSelected = () => {
    return this.data.count
  }

  getAll = () => {
    return this.data
  }
}

export const tableSelectedRowColumn = createDataBox(
  tableSelectedRowColumnStoreID,
  defaultState,
  SelectedRowColumnDataBox,
)
